'use client';

import { ChoiceButtonInput } from '@app/app/_components/Input/ChoiceButtonInput';
import { Message } from '@app/app/_components/Message/Message';
import { ClockIcon } from '@app/app/_icons/ClockIcon';
import { PaymentType } from '@app/domain/model/PaymentType';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseTransportInsuranceResult } from '@app/service/eshop/transport/actions/purchaseTransportInsurance';
import { formatEuroValue } from '@app/infrastructure/application/formatEuroValue';
import { useTranslations } from 'next-intl';
import { useMemo, useCallback } from 'react';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { Heading } from '@app/app/_components/Heading/Heading';
import { TransportPremiumsResponse } from '@app/domain/model/eshop/transport/TransportPremiumsResponse';
import { PaymentSchedule } from '@app/app/esales/transport/components/PaymentSchedule';
import { ProductDataRider } from '@app/domain/model/eshop/transport/ProductDataRider';
import { buildMergedPaymentSchedule } from '@app/service/eshop/transport/utils/buildMergedPaymentSchedule';

interface Props {
    formState: FormState<PurchaseTransportInsuranceResult>;
    selectedMtplPremium: Nullable<TransportPremiumsResponse>;
    selectedCascoPremium: Nullable<TransportPremiumsResponse>;
    selectedRiders: ProductDataRider[];
}

export function PaymentOptionsStep({ formState, selectedMtplPremium, selectedCascoPremium, selectedRiders }: Props) {
    const t = useTranslations();

    const totalAmount = useMemo(() => {
        if (!formState.data?.offers?.policies) return 0;
        return formState.data.offers.policies.reduce((sum, policy) => sum + policy.premiumAfterDiscount, 0);
    }, [formState.data?.offers]);

    const priceToPayNow = useMemo(() => {
        return formState.data?.offers?.priceToPayNow ?? 0;
    }, [formState.data?.offers?.priceToPayNow]);

    const ridersSum = useMemo(() => {
        return selectedRiders.reduce((total, rider) => total + rider.premiums.premium, 0);
    }, [selectedRiders]);

    const mergedPaymentSchedule = useMemo(() => {
        return buildMergedPaymentSchedule(selectedMtplPremium, selectedCascoPremium, ridersSum);
    }, [selectedMtplPremium, selectedCascoPremium, ridersSum]);

    const regularAmountPerPayment = useMemo(() => {
        return priceToPayNow;
    }, [priceToPayNow]);

    const renderDimText = useCallback(
        (chunks: React.ReactNode) => <span className="text-neutral-500">{chunks}</span>,
        [],
    );

    const renderBoldText = useCallback((chunks: React.ReactNode) => <span className="font-medium">{chunks}</span>, []);

    const paymentOptions = useMemo(() => {
        const options = [];
        const paymentCount = formState.data?.formData.paymentCount ?? 0;
        const hasMtplOnly = selectedMtplPremium && !selectedCascoPremium;
        const showOnlySubscription = hasMtplOnly && paymentCount !== 1;

        if (paymentCount >= 2) {
            options.push({
                value: PaymentType.Subscription,
                label: (
                    <div className="flex flex-col gap-3 font-medium">
                        <Heading className="font-normal" level="h3">
                            {t('eshop.general.subscription')}
                        </Heading>
                        <Paragraph size="medium">
                            {t.rich('eshop.general.paymentOptionSubtitle', {
                                amount: formatEuroValue(regularAmountPerPayment, true),
                                payments: formState.data?.formData.paymentCount,
                                dim: renderDimText,
                            })}
                        </Paragraph>
                    </div>
                ),
                details: (
                    <div className="flex w-full flex-col gap-4">
                        <div className="inline-flex items-center gap-2">
                            <ClockIcon className="size-6" />
                            <span>
                                {t.rich('eshop.general.paymentFrequencyHint', {
                                    payments: formState.data?.formData.paymentCount,
                                    b: renderBoldText,
                                })}
                            </span>
                        </div>
                        <PaymentSchedule schedule={mergedPaymentSchedule} />
                    </div>
                ),
            });
        }

        if (!showOnlySubscription && paymentCount >= 1 && paymentCount <= 4) {
            options.push({
                value: PaymentType.ManualPayment,
                label: (
                    <div className="flex flex-col gap-3 font-medium">
                        <Heading className="font-normal" level="h3">
                            {t('eshop.general.manualPayment')}
                        </Heading>
                        <Paragraph size="medium">
                            {t.rich('eshop.general.paymentOptionSubtitle', {
                                amount: formatEuroValue(priceToPayNow, true),
                                payments: formState.data?.formData.paymentCount,
                                dim: renderDimText,
                            })}
                        </Paragraph>
                    </div>
                ),
                details: (
                    <div className="flex w-full flex-col gap-4">
                        <div className="inline-flex items-center gap-2">
                            <ClockIcon className="size-6" />
                            <span>
                                {t.rich('eshop.general.paymentFrequencyHint', {
                                    payments: formState.data?.formData.paymentCount,
                                    b: renderBoldText,
                                })}
                            </span>
                        </div>

                        <PaymentSchedule schedule={mergedPaymentSchedule} />
                    </div>
                ),
            });
        }

        return options;
    }, [
        formState.data?.formData.paymentCount,
        t,
        regularAmountPerPayment,
        renderDimText,
        renderBoldText,
        mergedPaymentSchedule,
        priceToPayNow,
        selectedMtplPremium,
        selectedCascoPremium,
    ]);

    return (
        <div className="flex flex-col gap-8">
            <Message variant="success">
                <div className="flex flex-col gap-2">
                    <Paragraph size="medium">
                        {t('eshop.general.youHaveSelectedToPayTimesPerYear', {
                            count: formState.data?.formData.paymentCount,
                        })}
                    </Paragraph>
                    <Paragraph size="small">
                        {(formState.data?.formData.paymentCount ?? 1) === 1
                            ? t.rich('eshop.general.youHaveSelectedToPayTimesPerYearDescription', {
                                  payments: formState.data?.formData.paymentCount,
                                  single: formatEuroValue(priceToPayNow, true),
                                  total: formatEuroValue(totalAmount, true),
                                  b: renderBoldText,
                              })
                            : t.rich('eshop.general.totalAmountMultiplePayments', {
                                  total: formatEuroValue(totalAmount, true),
                                  payments: formState.data?.formData.paymentCount,
                                  b: renderBoldText,
                              })}
                    </Paragraph>
                </div>
            </Message>

            <ChoiceButtonInput name="paymentType" options={paymentOptions} />
        </div>
    );
}
